import { createSlice, type Action, type PayloadAction } from "@reduxjs/toolkit";
import type { EntryUIState, WishlistEntryModel, WishlistModel, WishlistOptionalPartModel } from "../types/model";
import { wishlistApi } from "../api/wishlistApi";
import type { WishlistsOnlyResponse } from "../api/fetchWishlists";
import { addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import { addMessage, addMessageAdvanced } from "~/features/toasts/redux/actions";
import { TranslationType } from "~/common/types/TranslationModel";
import {
  addToCartItemsFromBasicItems,
  type BasicAddToCartItem,
} from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { isKITProduct, type KITProductData, type ProductData } from "~/domains/product/types/ProductData";
import type { AppThunk, RootState } from "~/app/store";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { loadProductPna } from "~/domains/pna/pnaCommands";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { queryClient } from "~/app/queryClient";
import { normalizeWishlistPriceReference } from "~/features/wishlist/utils/priceReference";
import type { OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { COPY_WISHLIST_STATUS_PARAM, REMOVED_ITEMS } from "~/features/wishlist/wishlistUrlParams";

/**
 * Minimal UI state for the wishlist page.
 * Actual wishlist/entry data is stored in RTK Query cache (baseApi).
 */
export interface WishlistUIState {
  selectedWishlistId: string | null;
  selectedEntryIds: string[];
  entryUIState: Record<string, EntryUIState>;
  addAllClicked: boolean;
  /** Track which wishlists have had their details (price/availability) fetched */
  processedWishlistIds: string[];
}

const initialState: WishlistUIState = {
  selectedWishlistId: null,
  selectedEntryIds: [],
  entryUIState: {},
  addAllClicked: false,
  processedWishlistIds: [],
};

/**
 * Loads one wishlist entry's price + availability through the canonical PnA queries (A05
 * migration of the legacy `requestPriceData` / `requestAvailabilityData` dispatches). Entries
 * without a staged basic base are skipped — the legacy actions were a no-op for unknown products.
 * Transport errors resolve to an empty price list; the query cache keeps the error state for the
 * view-model banner derivations.
 */
const loadWishlistEntryPna = async (
  state: RootState,
  productCode: string,
  priceReference: string,
): Promise<PriceInfo[]> => {
  if (!state.products.basic[productCode]) {
    return [];
  }
  const { price } = await loadProductPna(queryClient, {
    productCode: state.products.basic[productCode].code,
    priceReference: normalizeWishlistPriceReference(priceReference),
  }).catch(() => ({ price: [] as PriceInfo[], availability: undefined }));
  return price;
};

/** First price packCode via the PnA query cache (replaces the `products.price` dictionary read). */
const firstPackCodeFromPna = async (state: RootState, productCode: string, priceReference: string) =>
  (await loadWishlistEntryPna(state, productCode, priceReference))[0]?.packCode ?? INVALID_PACKAGE_CODE;

const wishlistUISlice = createSlice({
  name: "wishlistUI",
  initialState,
  reducers: {
    setSelectedWishlist(state, action: PayloadAction<string | null>) {
      state.selectedWishlistId = action.payload;
      state.selectedEntryIds = []; // Clear selection when switching wishlists
    },
    setSelectedEntryIds(state, action: PayloadAction<string[]>) {
      state.selectedEntryIds = action.payload;
    },
    setEntryUIState(state, action: PayloadAction<{ entryId: string; packCode?: string; isSaleEnabled?: boolean }>) {
      const { entryId, packCode, isSaleEnabled } = action.payload;
      if (!state.entryUIState[entryId]) {
        state.entryUIState[entryId] = { packCode: packCode ?? "", isSaleEnabled: isSaleEnabled ?? false };
      } else {
        if (packCode !== undefined) {
          state.entryUIState[entryId].packCode = packCode;
        }
        if (isSaleEnabled !== undefined) {
          state.entryUIState[entryId].isSaleEnabled = isSaleEnabled;
        }
      }
    },
    setAddAllClicked(state, action: PayloadAction<boolean>) {
      state.addAllClicked = action.payload;
    },
    markWishlistProcessed(state, action: PayloadAction<string>) {
      if (!state.processedWishlistIds.includes(action.payload)) {
        state.processedWishlistIds.push(action.payload);
      }
    },
    initializeEntryUIState(state, action: PayloadAction<WishlistEntryModel[]>) {
      action.payload.forEach((entry) => {
        if (!state.entryUIState[entry.id]) {
          state.entryUIState[entry.id] = { packCode: "", isSaleEnabled: false };
        }
      });
    },
  },
  extraReducers: (builder) => {
    // Initialize entry UI state when wishlists are fetched
    builder.addMatcher(wishlistApi.endpoints.getWishlists.matchFulfilled, (state, action) => {
      const { wishlistEntries } = action.payload;
      wishlistEntries.forEach((entry) => {
        if (!state.entryUIState[entry.id]) {
          state.entryUIState[entry.id] = { packCode: "", isSaleEnabled: false };
        }
      });
    });
  },
});

export const {
  setSelectedWishlist,
  setSelectedEntryIds,
  setEntryUIState,
  setAddAllClicked,
  markWishlistProcessed,
  initializeEntryUIState,
} = wishlistUISlice.actions;

// ============================================================================
// Helper to get wishlist data from RTK Query cache
// ============================================================================

const selectWishlistsResult = wishlistApi.endpoints.getWishlists.select();

/** Get all wishlists from RTK Query cache */
export const selectCachedWishlists = (state: RootState): WishlistModel[] => {
  const result = selectWishlistsResult(state);
  return result.data?.wishlists ?? [];
};

/** Get wishlists as a map by ID */
export const selectCachedWishlistsMap = (state: RootState): Record<string, WishlistModel> => {
  const wishlists = selectCachedWishlists(state);
  return wishlists.reduce(
    (acc, wl) => {
      acc[wl.id] = wl;
      return acc;
    },
    {} as Record<string, WishlistModel>,
  );
};

/** Get all entries from RTK Query cache */
export const selectCachedEntries = (state: RootState): WishlistEntryModel[] => {
  const result = selectWishlistsResult(state);
  return result.data?.wishlistEntries ?? [];
};

/** Get entries as a map by ID */
export const selectCachedEntriesMap = (state: RootState): Record<string, WishlistEntryModel> => {
  const entries = selectCachedEntries(state);
  return entries.reduce(
    (acc, entry) => {
      acc[entry.id] = entry;
      return acc;
    },
    {} as Record<string, WishlistEntryModel>,
  );
};

/** Check if wishlists are loading */
export const selectIsWishlistsLoading = (state: RootState): boolean => {
  const result = selectWishlistsResult(state);
  return result.isLoading || result.isUninitialized;
};

/** Check if wishlists fetch failed */
export const selectIsWishlistsError = (state: RootState): boolean => {
  const result = selectWishlistsResult(state);
  return result.isError;
};

// ============================================================================
// Thunks
// ============================================================================

export const wishlistPageMounted = (): AppThunk<void> => async (dispatch, getState) => {
  // Handle URL params for copy wishlist status
  const params = new URLSearchParams(window.location.search);
  const copyWishlistStatus = params.get(COPY_WISHLIST_STATUS_PARAM);
  if (copyWishlistStatus !== null) {
    if (copyWishlistStatus === "true") {
      dispatch(addMessage("info", "page.wishlist.copyWishlist.success", "translation"));
    } else {
      dispatch(addMessage("error", "page.wishlist.copyWishlist.error", "translation"));
    }
  }

  const copyWishlistRemovedItems = params.get(REMOVED_ITEMS);
  if (copyWishlistRemovedItems !== null) {
    if (copyWishlistRemovedItems === "true") {
      dispatch(addMessage("info", "page.wishlist.copyWishlist.hasRemovedItems", "translation"));
    }
  }

  // Fetch wishlists via RTK Query
  // Note: Product data is populated by productsReducer listening to getWishlists.matchFulfilled
  let result: WishlistsOnlyResponse;
  try {
    result = await dispatch(wishlistApi.endpoints.getWishlists.initiate()).unwrap();
  } catch (error) {
    console.error("Failed to fetch wishlists", error);
    return;
  }

  // Auto-select first wishlist if none selected
  if (result.wishlists && result.wishlists.length > 0) {
    const selectedId = getState().wishlistUI.selectedWishlistId;
    const wishlistExists = selectedId && result.wishlists.some((wl) => wl.id === selectedId);

    if (!selectedId || !wishlistExists) {
      dispatch(selectWishlistThunk(result.wishlists[0].id));
    } else {
      dispatch(selectWishlistThunk(selectedId));
    }
  }

  const { loadCartAmounts } = await import("~/features/addToCart/redux/cartAmounts/actions");
  dispatch(loadCartAmounts() as unknown as Action<string>);
};

export const selectWishlistThunk =
  (wishlistId: string | null): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(setSelectedWishlist(wishlistId));
    if (wishlistId) {
      const state = getState();
      const processed = state.wishlistUI.processedWishlistIds.includes(wishlistId);
      if (!processed) {
        await dispatch(fetchWishlistDetails(wishlistId));
      }
    }
  };

export const fetchWishlistDetails =
  (wishlistId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const processed = state.wishlistUI.processedWishlistIds.includes(wishlistId);
    if (processed) {
      return;
    }

    dispatch(markWishlistProcessed(wishlistId));

    // Get wishlist and entries from cache
    const wishlistsMap = selectCachedWishlistsMap(state);
    const entriesMap = selectCachedEntriesMap(state);
    const wishlist = wishlistsMap[wishlistId];

    if (!wishlist) {
      return;
    }

    const entries = wishlist.entryIds.map((id) => entriesMap[id]).filter((e): e is WishlistEntryModel => !!e);

    if (entries.length < getRuntimeConfig().cartPnaThreshold) {
      await Promise.all(entries.map((entry) => loadWishlistEntryPna(state, entry.productCode, entry.priceReference)));
    } else {
      dispatch(
        addMessageAdvanced("info", {
          type: TranslationType.INTERNAL,
          value: "page.wishlist.message.abovePnaThreshold",
          params: { count: getRuntimeConfig().cartPnaThreshold },
        }),
      );
    }
  };

export const removeSelectedWishlistThunk = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const state = getState();
  const selectedWishlistId = state.wishlistUI.selectedWishlistId;
  if (!selectedWishlistId) {
    return;
  }

  const wishlistsMap = selectCachedWishlistsMap(state);
  const wishlist = wishlistsMap[selectedWishlistId];
  if (!wishlist) {
    return;
  }

  try {
    await dispatch(wishlistApi.endpoints.deleteWishlist.initiate(selectedWishlistId)).unwrap();

    // Select next available wishlist
    const wishlists = selectCachedWishlists(state);
    const remainingIds = wishlists.map((wl) => wl.id).filter((id) => id !== selectedWishlistId);
    dispatch(selectWishlistThunk(remainingIds[0] ?? null));

    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: "wishlistPage.globalMsg.deleted",
        params: { name: wishlist.name },
      }),
    );
  } catch (error) {
    console.error("Failed to delete wishlist", error);
  }
};

export const removeProductThunk =
  (entryId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const wishlistId = state.wishlistUI.selectedWishlistId;

    if (!entry || !wishlistId) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.removeEntry.initiate({
          id: wishlistId,
          productCode: entry.productCode,
          wishlistEntryNumber: entry.wishlistEntryNumber ?? 0,
        }),
      ).unwrap();

      dispatch(addMessage("info", "wishlistPage.globalMsg.applied", "translation"));
    } catch (error) {
      console.error("Failed to remove product from wishlist", error);
    }
  };

export const renameWishlistThunk =
  (wishlistId: string, newName: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const wishlistsMap = selectCachedWishlistsMap(getState());
    const wishlist = wishlistsMap[wishlistId];
    if (!wishlist) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.createOrUpdateWishlist.initiate({
          id: wishlistId,
          wishlistName: newName,
          description: wishlist.comment,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to rename wishlist", error);
    }
  };

export const updateProductThunk =
  (entryId: string, newProduct: ProductData): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const wishlistId = state.wishlistUI.selectedWishlistId;

    if (!entry || !wishlistId) {
      return;
    }

    dispatch(addBaseProductDataAction(newProduct));
    void loadWishlistEntryPna(getState(), newProduct.code, entry.priceReference);

    try {
      await dispatch(
        wishlistApi.endpoints.updateWishlistEntry.initiate({
          id: wishlistId,
          productCode: entry.productCode,
          newProductCode: newProduct.code,
          quantity: entry.quantity,
          wishlistEntryNumber: entry.wishlistEntryNumber ?? 0,
          comment: entry.comment,
          priceReference: entry.priceReference,
          optionalParts: entry.optionalParts,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to update product", error);
    }
  };

export const updateWishlistCommentThunk =
  (wishlistId: string, comment: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const wishlistsMap = selectCachedWishlistsMap(getState());
    const wishlist = wishlistsMap[wishlistId];
    if (!wishlist) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.createOrUpdateWishlist.initiate({
          id: wishlistId,
          wishlistName: wishlist.name,
          description: comment,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to update wishlist comment", error);
    }
  };

export const updateQuantityThunk =
  (entryId: string, quantity: number): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const wishlistId = state.wishlistUI.selectedWishlistId;

    if (!entry || !wishlistId) {
      return;
    }

    const product = state.products.basic[entry.productCode];
    let optionalParts = entry.optionalParts;

    if (quantity && isKITProduct(product)) {
      const kitProduct: KITProductData = product;
      const findQty = (productCode: string) =>
        kitProduct.optionalParts.find((part) => part.product.code === productCode)?.quantity ?? 1;
      optionalParts = optionalParts?.map((part) => ({ ...part, quantity: quantity! * findQty(part.productCode) }));
    }

    try {
      await dispatch(
        wishlistApi.endpoints.updateWishlistEntry.initiate({
          id: wishlistId,
          productCode: entry.productCode,
          quantity,
          wishlistEntryNumber: entry.wishlistEntryNumber ?? 0,
          comment: entry.comment,
          optionalParts,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to update quantity", error);
    }
  };

export const updatePriceRefThunk =
  (entryId: string, newPriceRef: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const wishlistId = state.wishlistUI.selectedWishlistId;

    if (!entry || !wishlistId) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.updateWishlistEntry.initiate({
          id: wishlistId,
          productCode: entry.productCode,
          quantity: entry.quantity,
          wishlistEntryNumber: entry.wishlistEntryNumber ?? 0,
          comment: entry.comment,
          priceReference: newPriceRef,
          optionalParts: entry.optionalParts,
        }),
      ).unwrap();
      void loadWishlistEntryPna(getState(), entry.productCode, newPriceRef);
    } catch (error) {
      console.error("Failed to update price reference", error);
    }
  };

export const updateCommentItemThunk =
  (entryId: string, comment: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const wishlistId = state.wishlistUI.selectedWishlistId;

    if (!entry || !wishlistId) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.updateWishlistEntry.initiate({
          id: wishlistId,
          productCode: entry.productCode,
          quantity: entry.quantity,
          wishlistEntryNumber: entry.wishlistEntryNumber ?? 0,
          comment,
          optionalParts: entry.optionalParts,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to update comment", error);
    }
  };

export const createWishlistThunk =
  (name: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const shouldSelectCreatedWishlist = selectCachedWishlists(getState()).length === 0;

    try {
      await dispatch(
        wishlistApi.endpoints.createOrUpdateWishlist.initiate({
          wishlistName: name,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to create wishlist", error);
      return;
    }

    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: "wishlistPage.globalMsg.created",
        params: { name },
      }),
    );

    try {
      // The create endpoint does not return the generated wishlist ID. This is the sole refresh
      // path for creates; it is non-subscribing and therefore does not retain query subscriptions.
      const { wishlists } = await dispatch(
        wishlistApi.endpoints.getWishlists.initiate(undefined, { forceRefetch: true, subscribe: false }),
      ).unwrap();
      if (shouldSelectCreatedWishlist) {
        const createdWishlist = wishlists
          .filter((wishlist) => wishlist.name === name)
          .sort((left, right) => right.lastModification - left.lastModification)[0];
        if (createdWishlist) {
          await dispatch(selectWishlistThunk(createdWishlist.id));
        }
      }
    } catch (error) {
      // The wishlist was created successfully. Keep the success message and avoid a duplicate
      // create if the read-back fails; the next normal wishlist load will include it.
      console.error("Failed to refresh wishlists after creation", error);
    }
  };

export const addToCartThunk =
  (entryId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const entriesMap = selectCachedEntriesMap(state);
    const entry = entriesMap[entryId];
    const uiState = state.wishlistUI.entryUIState[entryId];
    if (!entry || !uiState) {
      return;
    }

    const packCode = uiState.packCode || (await firstPackCodeFromPna(state, entry.productCode, entry.priceReference));

    await dispatch(
      addToCart({
        items: await dispatch(
          addToCartItemsFromBasicItems([
            {
              productCode: entry.productCode,
              packCode,
              quantity: entry.quantity,
              priceReference: entry.priceReference,
              isSale: uiState.isSaleEnabled,
              comment: entry.comment ?? "",
            },
          ]),
        ),
        page: "WISHLIST",
      }),
    );
    // analytics...
  };

export const addAllToCartThunk = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  dispatch(setAddAllClicked(true));
  const state = getState();
  const { selectedEntryIds, entryUIState } = state.wishlistUI;
  const entriesMap = selectCachedEntriesMap(state);

  if (selectedEntryIds.length === 0) {
    return;
  }

  const items = (
    await Promise.all(
      selectedEntryIds.map(async (id: string): Promise<BasicAddToCartItem | null> => {
        const entry = entriesMap[id];
        const uiState = entryUIState[id];
        if (!entry || !uiState) {
          return null;
        }

        const packCode =
          uiState.packCode || (await firstPackCodeFromPna(state, entry.productCode, entry.priceReference));

        return {
          productCode: entry.productCode,
          packCode,
          quantity: entry.quantity,
          priceReference: entry.priceReference,
          isSale: uiState.isSaleEnabled,
          comment: entry.comment ?? "",
        };
      }),
    )
  ).filter((item: BasicAddToCartItem | null): item is BasicAddToCartItem => item !== null);

  await dispatch(
    addToCart({
      items: await dispatch(addToCartItemsFromBasicItems(items)),
      page: "WISHLIST",
    }),
  );
  // sendWishlistAddSelectedToCartClick(...)
};

export const wishlistAfterScrollThunk =
  (oldLastSeen: number, newLastSeen: number): AppThunk<void> =>
  (dispatch, getState) => {
    if (oldLastSeen >= newLastSeen) {
      return;
    }

    const state = getState();
    const products = state.products.basic;
    const wishlistsMap = selectCachedWishlistsMap(state);
    const entriesMap = selectCachedEntriesMap(state);
    const wishlist = wishlistsMap[state.wishlistUI.selectedWishlistId!];
    if (!wishlist) {
      return;
    }

    const entryIds = wishlist.entryIds.slice(oldLastSeen, newLastSeen);
    void import("~/app/analytics/ga/ga").then(({ convertProductToImpression, sendWishlistProductListImpression }) => {
      const impressions = entryIds.flatMap((id, index) => {
        const entry = entriesMap[id];
        const product = entry ? products[entry.productCode] : undefined;
        if (!product) {
          return [];
        }
        const impression = convertProductToImpression({ basic: product });
        impression.list = "Wishlist";
        impression.position = 1 + oldLastSeen + index;
        return [impression];
      });

      sendWishlistProductListImpression(impressions);
    });
  };

export const addToWishlistThunk =
  (data: {
    id?: string;
    wishlistName?: string;
    productCode: string;
    quantity: number;
    priceReference?: string;
    optionalParts?: WishlistOptionalPartModel[];
  }): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch(wishlistApi.endpoints.addEntry.initiate(data)).unwrap();
    } catch (error) {
      console.error("Failed to add to wishlist", error);
    }
  };

export const addCartToWishlistThunk =
  (data: { id: string } | { wishlistName: string }): AppThunk<Promise<string>> =>
  async (dispatch) => {
    const result = await dispatch(wishlistApi.endpoints.addCart.initiate(data)).unwrap();
    return result.name;
  };

export const bulkAddToWishlistThunk =
  (data: {
    wishlistId?: string;
    wishlistName?: string;
    products: { code: string; quantity: number }[];
  }): AppThunk<Promise<string>> =>
  async (dispatch, getState) => {
    try {
      await dispatch(wishlistApi.endpoints.bulkAddEntry.initiate(data)).unwrap();

      if (data.wishlistName) {
        return data.wishlistName;
      }
      if (data.wishlistId) {
        const wishlistsMap = selectCachedWishlistsMap(getState());
        const wishlist = wishlistsMap[data.wishlistId];
        return wishlist?.name ?? "";
      }
      return "";
    } catch (error) {
      console.error("Failed to bulk add to wishlist", error);
      throw error;
    }
  };

export const quickEntryToCurrentWishlistThunk =
  (data: { code: string; quantity: number }[]): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const wishlistId = getState().wishlistUI.selectedWishlistId;
    if (!wishlistId) {
      return;
    }

    try {
      await dispatch(
        wishlistApi.endpoints.bulkAddEntry.initiate({
          wishlistId,
          products: data,
        }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to quick entry to wishlist", error);
    }
  };

export const fetchWishDetailThunk =
  (productCode: string, priceReference: string): AppThunk<Promise<void>> =>
  async (_dispatch, getState) => {
    await loadWishlistEntryPna(getState(), productCode, priceReference);
  };

export const fetchWishlistNames = (): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(wishlistApi.endpoints.getWishlists.initiate()).unwrap();
};

export const addOrderDetailsToWishlistThunk =
  (data: { wishlistId?: string; wishlistName?: string }, orderItems: OrderLineItemData[]): AppThunk<Promise<string>> =>
  async (dispatch, getState) => {
    const products = orderItems.map((item) => ({ code: item.product.code, quantity: item.qty }));
    await dispatch(bulkAddToWishlistThunk({ ...data, products }));

    if (data.wishlistName) {
      return data.wishlistName;
    }
    if (data.wishlistId) {
      const wishlistsMap = selectCachedWishlistsMap(getState());
      const wishlist = wishlistsMap[data.wishlistId];
      return wishlist?.name ?? "";
    }
    return "";
  };

export default wishlistUISlice.reducer;
